import React, {useState} from 'react';
import {View, Text, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Image, ActivityIndicator} from 'react-native';
import {Lineup, Player} from '../../types/diretta';
import {PlayerIncidents} from '../../services/diretta';
import {useTheme} from '../../hooks/useTheme';

const {width: SCREEN_WIDTH} = Dimensions.get('window');
const PITCH_WIDTH = SCREEN_WIDTH - 32;
const PITCH_HEIGHT = PITCH_WIDTH * 1.3;

interface LineupsTabProps {
  lineups: Lineup | null;
  homeTeam: string;
  awayTeam: string;
  loading?: boolean;
  playerIncidents?: PlayerIncidents;
}

export const LineupsTab: React.FC<LineupsTabProps> = ({
  lineups,
  homeTeam,
  awayTeam,
  loading,
  playerIncidents = {},
}) => {
  const {colors, isDark} = useTheme();
  const [activeTeam, setActiveTeam] = useState<'home' | 'away'>('away'); // Default to Fiorentina

  // Get rating color based on value (red < 6.0, orange 6.0-7.0, green > 7.0)
  const getRatingColor = (rating: number): string => {
    if (rating < 6.0) return '#ef4444'; // Red
    if (rating < 7.0) return '#f97316'; // Orange
    return '#22c55e'; // Green
  };

  // Safely get player display name - handles various API formats
  const getPlayerName = (player: Player): string => {
    // If short_name exists, prefer it (already formatted)
    if (player.short_name && typeof player.short_name === 'string') {
      return player.short_name;
    }
    // If name is a string, use it
    if (typeof player.name === 'string') {
      return player.name;
    }
    // Fallback to number
    return `#${player.number}`;
  };

  // Get name in "F. Lastname" format for display on pitch
  const getShortName = (player: Player): string => {
    const fullName = getPlayerName(player);
    // Remove captain/goalkeeper indicators like (C) or (P)
    const cleanName = fullName.replace(/\s*\([CP]\)\s*/g, '').trim();
    const parts = cleanName.split(' ');
    
    if (parts.length >= 2) {
      // First initial + last name: "M. Rossi"
      const firstInitial = parts[0].charAt(0).toUpperCase();
      const lastName = parts[parts.length - 1];
      return `${firstInitial}. ${lastName}`;
    }
    return cleanName;
  };

  // Check if formation is a valid pattern (like "4-3-3" or "3-5-2")
  const isValidFormation = (formation: string) => {
    return /^\d+-\d+(-\d+)*$/.test(formation);
  };

  // Parse formation string to get rows of players
  const parseFormation = (formation: string): number[] => {
    if (!isValidFormation(formation)) return [4, 4, 2]; // Default 4-4-2
    return formation.split('-').map(Number);
  };

  // Get player positions on the pitch based on formation
  const getPlayerPositions = (players: Player[], formation: string) => {
    const rows = parseFormation(formation);
    const positions: {player: Player; row: number; col: number; total: number}[] = [];
    
    // First player is goalkeeper
    if (players[0]) {
      positions.push({player: players[0], row: 0, col: 0, total: 1});
    }
    
    // Distribute remaining players by formation rows
    let playerIndex = 1;
    rows.forEach((playersInRow, rowIndex) => {
      for (let i = 0; i < playersInRow && playerIndex < players.length; i++) {
        positions.push({
          player: players[playerIndex],
          row: rowIndex + 1,
          col: i,
          total: playersInRow,
        });
        playerIndex++;
      }
    });
    
    return positions;
  };

  if (loading) {
    return (
      <View style={[styles.emptyContainer, {backgroundColor: colors.surface}]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.emptyText, {color: colors.textSecondary, marginTop: 12}]}>Caricamento formazioni...</Text>
      </View>
    );
  }

  if (!lineups) {
    return (
      <View style={[styles.emptyContainer, {backgroundColor: colors.surface}]}>
        <Text style={[styles.emptyTitle, {color: colors.textPrimary}]}>Formazioni non disponibili</Text>
        <Text style={[styles.emptyText, {color: colors.textSecondary}]}>
          Le formazioni saranno disponibili prima del fischio d'inizio.
        </Text>
      </View>
    );
  }

  const currentTeam = activeTeam === 'home' ? lineups.home : lineups.away;
  
  // Check for missing or empty lineup data
  if (!currentTeam || !currentTeam.players || currentTeam.players.length === 0) {
    return (
      <View style={[styles.container, {backgroundColor: colors.background}]}>
        <View style={[styles.toggleContainer, {backgroundColor: isDark ? colors.surface : '#f0f0f0'}]}>
          <TouchableOpacity
            style={[styles.toggleButton, activeTeam === 'home' && {backgroundColor: colors.primary}]}
            onPress={() => setActiveTeam('home')}
          >
            <Text style={[styles.toggleText, {color: isDark ? colors.textSecondary : '#666'}, activeTeam === 'home' && styles.activeToggleText]}>
              {homeTeam}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, activeTeam === 'away' && {backgroundColor: colors.primary}]}
            onPress={() => setActiveTeam('away')}
          >
            <Text style={[styles.toggleText, {color: isDark ? colors.textSecondary : '#666'}, activeTeam === 'away' && styles.activeToggleText]}>
              {awayTeam}
            </Text>
          </TouchableOpacity>
        </View>
        <View style={[styles.emptyContainer, {backgroundColor: colors.surface}]}>
          <Text style={[styles.emptyText, {color: colors.textSecondary}]}>
            Formazione non disponibile
          </Text>
        </View>
      </View>
    );
  }

  const formation = currentTeam.formation || '';
  const formationRows = parseFormation(formation);
  const totalRows = formationRows.length + 1; // +1 for goalkeeper
  const playerPositions = getPlayerPositions(currentTeam.players, formation);

  const renderPitchPlayer = (position: {player: Player; row: number; col: number; total: number}) => {
    const {player, row, col, total} = position;
    const rowHeight = PITCH_HEIGHT / (totalRows + 0.5);
    const top = PITCH_HEIGHT - (row + 1) * rowHeight; // Invert so GK is at bottom
    const horizontalSpacing = PITCH_WIDTH / (total + 1);
    const left = PITCH_WIDTH - (horizontalSpacing * (col + 1)) - 25; // Invert: Start from right
    
    // Safely get number (could be object in some API responses)
    const playerNum = typeof player.number === 'number' ? player.number : String(player.number || '');
    const isCaptain = player.captain === true;
    const hasRating = player.rating && player.rating > 0;
    const hasImage = player.image && typeof player.image === 'string';
    
    // Get player incidents
    const incidents = player.player_id ? playerIncidents[player.player_id] : null;
    
    // Build incidents array (goals and subs only, no assists)
    const hasGoals = incidents && incidents.goals > 0;
    const hasSub = incidents?.substitutedOut;
    
    return (
      <View
        key={`player-${playerNum}-${row}-${col}`}
        style={[styles.pitchPlayer, {top, left}]}
      >
        {/* Card badge - top left (no text, just color) */}
        {incidents?.yellowCard && !incidents?.redCard && (
          <View style={[styles.cardBadgeSmall, styles.cardBadgeTopLeft, {backgroundColor: '#eab308'}]} />
        )}
        {incidents?.redCard && (
          <View style={[styles.cardBadgeSmall, styles.cardBadgeTopLeft, {backgroundColor: '#dc2626'}]} />
        )}
        
        {/* Rating badge - top right */}
        {hasRating && (
          <View style={[styles.ratingBadge, {backgroundColor: getRatingColor(player.rating!)}]}>
            <Text style={styles.ratingText}>{player.rating!.toFixed(1)}</Text>
          </View>
        )}
        
        {hasImage ? (
          <View style={{width: 36, alignItems: 'center'}}>
            <Image 
              source={{uri: player.image}} 
              style={styles.playerImage}
            />
            <View style={styles.badgeContainer}>
              <View style={styles.numberBadge}>
                <Text style={styles.numberBadgeText}>{playerNum}</Text>
              </View>
            </View>
          </View>
        ) : (
          <View style={[styles.playerCircle, {backgroundColor: isDark ? '#374151' : '#4b5563'}]}>
            <Text style={styles.playerNumber}>{playerNum}</Text>
          </View>
        )}
        
        {/* Incident icons - bottom left of circle, stacked vertically */}
        {(hasGoals || hasSub) && (
          <View style={styles.incidentStackVertical}>
            {hasGoals && (
              <View style={styles.goalIconBadge}>
                <Text style={styles.goalIcon}>⚽</Text>
              </View>
            )}
            {hasSub && (
              <View style={styles.subIconBadge}>
                <Text style={styles.subIcon}>⇄</Text>
              </View>
            )}
          </View>
        )}
        
        <Text style={styles.playerNamePitch} numberOfLines={1}>
          {getShortName(player)}
          {isCaptain && ' (C)'}
        </Text>
      </View>
    );
  };

  return (
    <ScrollView style={[styles.container, {backgroundColor: colors.background}]}>
      {/* Team toggle */}
      <View style={[styles.toggleContainer, {backgroundColor: isDark ? colors.surface : '#f0f0f0'}]}>
        <TouchableOpacity
          style={[styles.toggleButton, activeTeam === 'home' && {backgroundColor: colors.primary}]}
          onPress={() => setActiveTeam('home')}
        >
          <Text style={[styles.toggleText, {color: isDark ? colors.textSecondary : '#666'}, activeTeam === 'home' && styles.activeToggleText]}>
            {homeTeam}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.toggleButton, activeTeam === 'away' && {backgroundColor: colors.primary}]}
          onPress={() => setActiveTeam('away')}
        >
          <Text style={[styles.toggleText, {color: isDark ? colors.textSecondary : '#666'}, activeTeam === 'away' && styles.activeToggleText]}>
            {awayTeam}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Pitch Visualization */}
      <View style={styles.pitchContainer}>
        <View style={[styles.pitch, {backgroundColor: isDark ? '#1a472a' : '#2e8b47'}]}>
          {/* Formation badge - inside pitch top-left */}
          {isValidFormation(formation) && (
            <View style={styles.formationBadgeInside}>
              <Text style={styles.formationTextSmall}>Modulo {formation}</Text>
            </View>
          )}
          
          {/* Pitch markings */}
          <View style={styles.pitchCenterCircle} />
          <View style={styles.pitchHalfLine} />
          <View style={[styles.pitchGoalBox, styles.pitchGoalBoxTop]} />
          <View style={[styles.pitchGoalBox, styles.pitchGoalBoxBottom]} />
          <View style={[styles.pitchPenaltyBox, styles.pitchPenaltyBoxTop]} />
          <View style={[styles.pitchPenaltyBox, styles.pitchPenaltyBoxBottom]} />
          
          {/* Players */}
          {playerPositions.map(renderPitchPlayer)}
        </View>
      </View>

      {/* Bench Section - Vertical List */}
      {currentTeam.substitutes && currentTeam.substitutes.length > 0 && (
        <View style={styles.benchSection}>
          <Text style={[styles.sectionTitle, {color: colors.textPrimary}]}>Panchina</Text>
          <View style={[styles.benchList, {backgroundColor: colors.surface}]}>
            {currentTeam.substitutes.map((player, index) => {
              const playerNum = typeof player.number === 'number' ? player.number : String(player.number || '');
              const hasImage = player.image && typeof player.image === 'string';
              const hasRating = player.rating && player.rating > 0;
              const incidents = player.player_id ? playerIncidents[player.player_id] : null;
              
              return (
                <View key={`bench-${index}`} style={[styles.benchPlayerRow, {borderBottomColor: colors.border}]}>
                  {/* Player image or fallback circle */}
                  {hasImage ? (
                    <Image 
                      source={{uri: player.image}} 
                      style={styles.benchPlayerImage}
                    />
                  ) : (
                    <View style={[styles.benchNumberCircle, {backgroundColor: isDark ? '#374151' : '#6b7280'}]}>
                      <Text style={styles.benchNumberText}>{playerNum}</Text>
                    </View>
                  )}
                  {/* Number + Name */}
                  <Text style={[styles.benchPlayerName, {color: colors.textPrimary}]}>
                    {playerNum} {getShortName(player)}
                  </Text>
                  {/* Event indicators */}
                  <View style={styles.benchBadgesContainer}>
                    {incidents?.substitutedIn && (
                      <View style={[styles.benchEventBadge, {backgroundColor: '#22c55e'}]}>
                        <Text style={styles.benchEventText}>IN</Text>
                      </View>
                    )}
                    {incidents && incidents.goals > 0 && (
                      <Text style={styles.benchGoalIcon}>⚽</Text>
                    )}
                    {incidents?.yellowCard && (
                      <View style={[styles.benchCardBadge, {backgroundColor: '#eab308'}]} />
                    )}
                    {incidents?.redCard && (
                      <View style={[styles.benchCardBadge, {backgroundColor: '#dc2626'}]} />
                    )}
                  </View>
                  {/* Color-coded rating */}
                  {hasRating && (
                    <View style={[styles.benchRatingBadge, {backgroundColor: getRatingColor(player.rating!)}]}>
                      <Text style={styles.benchRatingText}>{player.rating!.toFixed(1)}</Text>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* Coach Section */}
      {currentTeam.coach && (
        <View style={[styles.coachSection, {backgroundColor: colors.surface, borderColor: colors.border}]}>
          <View style={[styles.coachIcon, {backgroundColor: colors.primary}]}>
            <Text style={styles.coachIconText}>A</Text>
          </View>
          <View style={styles.coachInfo}>
            <Text style={[styles.coachLabel, {color: colors.textSecondary}]}>Allenatore</Text>
            <Text style={[styles.coachName, {color: colors.textPrimary}]}>{currentTeam.coach}</Text>
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 4,
    marginBottom: 16,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 6,
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  activeToggleText: {
    color: '#fff',
  },
  formationBadge: {
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
    marginBottom: 16,
  },
  formationText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  pitchContainer: {
    marginBottom: 20,
    borderRadius: 12,
    overflow: 'hidden',
  },
  pitch: {
    width: PITCH_WIDTH,
    height: PITCH_HEIGHT,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
    position: 'relative',
  },
  pitchCenterCircle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
    marginLeft: -30,
    marginTop: -30,
  },
  pitchHalfLine: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  pitchGoalBox: {
    position: 'absolute',
    left: '50%',
    marginLeft: -30,
    width: 60,
    height: 20,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  pitchGoalBoxTop: {
    top: 0,
    borderTopWidth: 0,
  },
  pitchGoalBoxBottom: {
    bottom: 0,
    borderBottomWidth: 0,
  },
  pitchPenaltyBox: {
    position: 'absolute',
    left: '50%',
    marginLeft: -60,
    width: 120,
    height: 40,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  pitchPenaltyBoxTop: {
    top: 0,
    borderTopWidth: 0,
  },
  pitchPenaltyBoxBottom: {
    bottom: 0,
    borderBottomWidth: 0,
  },
  pitchPlayer: {
    position: 'absolute',
    alignItems: 'center',
    width: 50,
  },
  playerCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  playerNumber: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  playerNamePitch: {
    color: '#fff',
    fontSize: 9, // Reduced from 10
    fontWeight: '600',
    textShadowColor: 'rgba(0,0,0,0.8)',
    textShadowOffset: {width: 1, height: 1},
    textShadowRadius: 2,
    textAlign: 'center',
    maxWidth: 60,
    marginTop: 8,
  },
  playerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  incidentStackVertical: {
    position: 'absolute',
    bottom: 16,
    left: -8,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
  },
  goalIconBadge: {
    width: 12,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  goalIcon: {
    fontSize: 10,
  },
  subIconBadge: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#dc2626',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subIcon: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },
  ratingBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
    zIndex: 1,
  },
  ratingText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  cardBadge: {
    position: 'absolute',
    width: 14,
    height: 18,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  cardBadgeSmall: {
    position: 'absolute',
    width: 8,
    height: 10,
    borderRadius: 1,
    zIndex: 2,
  },
  cardBadgeTopLeft: {
    top: -2,
    left: -2,
  },
  cardText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },
  incidentStack: {
    position: 'absolute',
    bottom: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  stackBadge: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  goalBadgeSmall: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#000',
  },
  assistBadgeSmall: {
    backgroundColor: '#3b82f6',
  },
  subBadgeSmall: {
    backgroundColor: '#ef4444',
  },
  goalBadge: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  goalText: {
    fontSize: 8,
    fontWeight: '700',
  },
  assistBadge: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#3b82f6',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  assistText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },
  subBadge: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  subOutText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  badgeBottomRight: {
    bottom: 22,
    right: -3,
  },
  badgeBottomLeft: {
    bottom: 22,
    left: -3,
  },
  badgeBottomCenter: {
    bottom: 22,
    left: '50%',
    marginLeft: -7,
  },
  benchBadgesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginRight: 8,
  },
  benchEventBadge: {
    width: 20,
    height: 16,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  benchEventText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },
  benchCardBadge: {
    width: 10,
    height: 14,
    borderRadius: 2,
  },
  benchGoalIcon: {
    fontSize: 12,
  },
  formationBadgeInside: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    zIndex: 10,
  },
  formationTextSmall: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  benchSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  benchList: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  benchPlayerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
  },
  benchNumberCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  benchNumberText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  benchPlayerImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  benchPlayerName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  },
  benchRating: {
    fontSize: 12,
    fontWeight: '600',
  },
  benchRatingBadge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  benchRatingText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  playerImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  coachSection: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  coachIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  coachIconText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  coachInfo: {
    flex: 1,
  },
  coachLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  coachName: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    padding: 30,
    alignItems: 'center',
    margin: 16,
    borderRadius: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  badgeContainer: {
    position: 'absolute',
    bottom: -6,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  numberBadge: {
    backgroundColor: '#333',
    borderRadius: 8,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderWidth: 1,
    borderColor: '#fff',
    minWidth: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  numberBadgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
  },

});
